import React, { useCallback, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { reportsAPI } from '../../services/api';
import useReport from './shared/useReport';
import ChartPanel from './shared/ChartPanel';
import ReportTable from './shared/ReportTable';
import ReportPrintLayout from './shared/ReportPrintLayout';
import PrintButton from './shared/PrintButton';
import DateRangeFilter, { getPresetRange } from './shared/DateRangeFilter';
import { PALETTE, axisProps, fmtMoney, fmtMoneyShort, tooltipStyle, barColor } from './shared/chartTheme';
import useDrill from './shared/useDrill';

// Where the purchasing money goes, vendor by vendor, plus every purchase-price change inside the
// range so a quiet rate increase never slips through unnoticed.

const VENDOR_COLUMNS = [
  { key: 'vendor', label: 'Vendor' },
  { key: 'receipts', label: 'Receipts', align: 'right' },
  { key: 'lines', label: 'Lines', align: 'right' },
  { key: 'totalValue', label: 'Purchased', align: 'right', render: (r) => <span className="font-semibold">{fmtMoney(r.totalValue)}</span>, csv: (r) => r.totalValue },
  { key: 'firstPurchase', label: 'First' },
  { key: 'lastPurchase', label: 'Last' },
];

const PRICE_COLUMNS = [
  { key: 'medicine', label: 'Medicine' },
  { key: 'vendor', label: 'Vendor' },
  { key: 'date', label: 'Date' },
  { key: 'oldPrice', label: 'Old Price', align: 'right', render: (r) => fmtMoney(r.oldPrice) },
  { key: 'newPrice', label: 'New Price', align: 'right', render: (r) => fmtMoney(r.newPrice) },
  {
    key: 'pctChange', label: 'Change', align: 'right',
    render: (r) => (
      <span className={Number(r.pctChange) > 0 ? 'text-rose-600 font-semibold' : 'text-emerald-600 font-semibold'}>
        {Number(r.pctChange) > 0 ? '+' : ''}{r.pctChange}%
      </span>
    ),
    csv: (r) => r.pctChange,
  },
];

const VendorPurchasesReport = () => {
  const drill = useDrill();
  const [range, setRange] = useState(() => getPresetRange('thisYear'));
  const [preset, setPreset] = useState('thisYear');

  const fetcher = useCallback(
    () => reportsAPI.getVendorPurchases({ from: range.from, to: range.to }),
    [range]
  );
  const { data, loading } = useReport(fetcher);

  const byVendor = (data?.byVendor || []).map((r) => ({ ...r, totalValue: Number(r.totalValue) }));

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[300px]">
          <DateRangeFilter value={range} preset={preset} onChange={(r, p) => { setRange(r); setPreset(p); }} />
        </div>
        <div className="pt-4"><PrintButton /></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ChartPanel title="Purchases by vendor">
          {loading || byVendor.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-16">
              {loading ? 'Loading' : 'No stock receipts in the selected range'}
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(200, byVendor.length * 40)}>
              <BarChart data={byVendor} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" horizontal={false} />
                <XAxis type="number" {...axisProps} tickFormatter={fmtMoneyShort} />
                <YAxis type="category" dataKey="vendor" {...axisProps} width={150} />
                <Tooltip {...tooltipStyle} formatter={(v) => fmtMoney(v)} />
                <Bar dataKey="totalValue" name="Purchased" radius={[0, 4, 4, 0]} barSize={18}>
              {byVendor.map((_, i) => <Cell key={i} fill={barColor(i)} />)}
            </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartPanel>
        <ChartPanel title="Monthly purchase total">
          {(data?.monthly || []).length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-16">No stock receipts in the selected range</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={data.monthly} margin={{ left: 8, right: 16 }}>
                <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" vertical={false} />
                <XAxis dataKey="bucket" {...axisProps} />
                <YAxis {...axisProps} tickFormatter={fmtMoneyShort} />
                <Tooltip {...tooltipStyle} formatter={(v) => fmtMoney(v)} />
                <Line type="monotone" dataKey="totalValue" name="Purchased" stroke={PALETTE[1]} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartPanel>
      </div>

      <ReportTable
        onRowClick={(r) => drill.open({ type: 'vendor', value: r.vendor, from: range.from, to: range.to })}
        title="Vendors"
        columns={VENDOR_COLUMNS}
        rows={data?.byVendor || []}
        csvName={`vendor-purchases-${range.from}-to-${range.to}`}
        emptyText="No stock receipts in the selected range"
      />
      <ReportTable
        onRowClick={(r) => drill.open({ type: 'stockMedicine', value: r.medicine })}
        title="Purchase price changes in range"
        columns={PRICE_COLUMNS}
        rows={data?.priceChanges || []}
        csvName={`price-changes-${range.from}-to-${range.to}`}
        emptyText="No purchase price changed in the selected range"
        footnote="A change compares each receipt line against the previous purchase of the same medicine, whichever vendor supplied it."
      />

      <ReportPrintLayout
        title="Vendor Purchases"
        subtitle={`Period: ${range.from} to ${range.to}`}
        tables={[
          { title: 'Vendors', columns: VENDOR_COLUMNS, rows: data?.byVendor || [] },
          { title: 'Purchase price changes', columns: PRICE_COLUMNS, rows: data?.priceChanges || [] },
        ]}
      />

      {drill.node}
    </div>
  );
};

export default VendorPurchasesReport;
